import React from "react";
import "./styles/Skeleton.css";

export const Skeleton = ({
  variant = "line",
  width,
  height,
  size,
  className = "",
  style,
  ...rest
}) => {
  const resolvedStyle = { ...style };
  if (width !== undefined) resolvedStyle.width = width;
  if (height !== undefined) resolvedStyle.height = height;
  if (variant === "circle" && size !== undefined) {
    resolvedStyle.width = size;
    resolvedStyle.height = size;
  }
  return (
    <span
      className={`skeleton skeleton--${variant} ${className}`.trim()}
      style={resolvedStyle}
      aria-hidden="true"
      {...rest}
    />
  );
};

export const SkeletonCard = ({ className = "", lines = 3 }) => (
  <div
    className={`skeleton-card ${className}`.trim()}
    aria-hidden="true"
  >
    <Skeleton variant="block" />
    <Skeleton variant="line" className="skeleton-card__meta" />
    <Skeleton variant="line" className="skeleton-card__title" />
    <Skeleton variant="line" className="skeleton-card__title--short" />
    {Array.from({ length: Math.max(0, lines) }).map((_, i) => (
      <Skeleton
        key={i}
        variant="line"
        className={`skeleton-card__line ${
          i === 1
            ? "skeleton-card__line--l2"
            : i === 2
            ? "skeleton-card__line--l3"
            : ""
        }`.trim()}
      />
    ))}
    <Skeleton variant="line" className="skeleton-card__cta" />
  </div>
);

export const SkeletonRow = ({ cols = 4, widths, className = "" }) => {
  const columns = widths
    ? widths
    : Array.from({ length: cols }).map(() => "1fr");
  return (
    <div
      className={`skeleton-row ${className}`.trim()}
      style={{ gridTemplateColumns: columns.join(" ") }}
      aria-hidden="true"
    >
      {Array.from({ length: columns.length }).map((_, i) => (
        <Skeleton key={i} variant="line" />
      ))}
    </div>
  );
};

export const SkeletonImage = ({
  ratio = "16/9",
  width = "100%",
  className = "",
  style,
  rounded = true,
}) => (
  <Skeleton
    variant="block"
    className={className}
    style={{
      width,
      aspectRatio: ratio,
      height: "auto",
      borderRadius: rounded ? "var(--fw-r-md)" : 0,
      ...style,
    }}
  />
);

export const SkeletonText = ({
  lines = 3,
  widths,
  lineHeight = 12,
  gap = 10,
  className = "",
}) => {
  const resolvedWidths =
    widths ||
    Array.from({ length: lines }).map((_, i, arr) =>
      i === arr.length - 1 ? "65%" : i % 2 === 0 ? "100%" : "95%"
    );
  return (
    <div
      className={className}
      style={{ display: "grid", gap }}
      aria-hidden="true"
    >
      {resolvedWidths.map((w, i) => (
        <Skeleton key={i} variant="line" width={w} height={lineHeight} />
      ))}
    </div>
  );
};

export const SkeletonStatValue = ({ width = 80, height = 28 }) => (
  <Skeleton variant="line" width={width} height={height} />
);

export const SkeletonMediaRow = ({ className = "" }) => (
  <div
    className={`skeleton-media-row ${className}`.trim()}
    aria-hidden="true"
  >
    <Skeleton variant="circle" />
    <div className="skeleton-media-row__text">
      <Skeleton variant="line" />
      <Skeleton variant="line" />
    </div>
  </div>
);

export const SkeletonList = ({
  count = 4,
  variant = "stack",
  children,
  className = "",
  ariaLabel = "Loading",
}) => {
  const items = React.Children.count(children)
    ? Array.from({ length: count }).map((_, i) =>
        React.cloneElement(
          React.Children.toArray(children)[0],
          { key: i }
        )
      )
    : Array.from({ length: count }).map((_, i) => (
        <SkeletonRow key={i} />
      ));
  return (
    <div
      className={`skeleton-list ${
        variant === "grid" ? "skeleton-list--grid" : ""
      } ${className}`.trim()}
      role="status"
      aria-busy="true"
      aria-label={ariaLabel}
    >
      {items}
    </div>
  );
};

export default Skeleton;
